import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, PackageSearch, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";
import { TrackingTimeline } from "../components/TrackingTimeline";

export default function Track() {
  const [params] = useSearchParams();
  const [query, setQuery] = useState(params.get("order") || "");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const lookup = async (num) => {
    const n = (num || query).trim();
    if (!n) return;
    setLoading(true); setError(""); setOrder(null);
    try {
      const { data } = await api.get(`/fx/track/${n}`);
      setOrder(data);
    } catch {
      setError("No order found with that number. Please check and try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (params.get("order")) lookup(params.get("order"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-14">
      <div className="text-center mb-10">
        <div className="w-14 h-14 rounded-full bg-[#306D29]/10 text-[#306D29] flex items-center justify-center mx-auto mb-5">
          <PackageSearch size={26} />
        </div>
        <h1 className="font-display text-4xl tracking-tight mb-2">Track your order</h1>
        <p className="text-[#5C5C5C]">Enter your order number (e.g. GB-2026-000245) to see live status.</p>
      </div>

      <div className="flex gap-3 mb-10">
        <input data-testid="track-input" value={query} onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && lookup()}
          placeholder="GB-2026-000245"
          className="flex-1 bg-white border border-black/10 rounded-full px-5 py-3.5 focus:ring-2 focus:ring-[#306D29] focus:outline-none" />
        <button data-testid="track-btn" onClick={() => lookup()} disabled={loading}
          className="bg-[#306D29] hover:bg-[#0D530E] text-white font-semibold px-6 rounded-full flex items-center gap-2"
          style={{ transition: "background-color 0.2s ease" }}>
          <Search size={18} /> Track
        </button>
      </div>

      {error && <p className="text-center text-[#306D29]" data-testid="track-error">{error}</p>}

      {order && (
        <div className="bg-white border border-black/5 rounded-3xl p-7" data-testid="track-result">
          <div className="flex flex-wrap justify-between gap-2 items-center mb-6 pb-6 border-b border-black/5">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#5C5C5C]">Order</p>
              <p className="font-display text-2xl">{order.order_number}</p>
            </div>
            {order.total_ghs != null && (
              <div className="text-right">
                <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#5C5C5C]">Total</p>
                <p className="font-display text-2xl text-[#306D29]">GH₵ {Number(order.total_ghs).toLocaleString()}</p>
              </div>
            )}
          </div>
          {order.payment_status !== "paid" ? (
            <p className="text-[#D9933B] font-medium">Awaiting payment confirmation.</p>
          ) : (
            <>
              <div className="rounded-2xl bg-[#0D530E] text-[#FBF5DD] p-5 mb-6" data-testid="track-status-banner">
                <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#E7E1B1]">Your order is currently</p>
                <p className="font-display text-2xl mt-1">{order.current_status}</p>
                <p className="text-sm text-[#FBF5DD]/85 mt-2">{order.message}</p>
                {order.eta && <p className="text-sm text-[#FBF5DD]/85 mt-1">Estimated delivery: <b className="text-[#E7E1B1]">{new Date(order.eta).toLocaleDateString(undefined, { day: "numeric", month: "long" })}</b></p>}
              </div>
              {order.issue_message && (
                <div className="rounded-2xl bg-[#D9933B]/12 border border-[#D9933B]/30 text-[#B5771F] p-4 mb-6 flex items-center gap-3" data-testid="track-issue">
                  <AlertTriangle size={18} /> <span className="text-sm font-medium">{order.issue_message}</span>
                </div>
              )}
              <TrackingTimeline steps={order.steps} currentStep={order.public_step} />
              {order.delivery_proof_url && (
                <div className="mt-6 pt-6 border-t border-black/5" data-testid="track-delivery-proof">
                  <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#5C5C5C] mb-2">Proof of delivery</p>
                  <img src={order.delivery_proof_url} alt="Proof of delivery" className="rounded-2xl w-full max-h-80 object-cover" />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
